/**
 * Finding the things in plain text that should not be plain: links, emails, and people.
 *
 * Rich text is handled elsewhere — the API turns bare URLs into real anchors on write, so a
 * description or a progress note arrives with the markup already in it. This is for the
 * fields that are genuinely plain and always were: comments, blocked and unblock notes, and
 * anything else showing text a person typed.
 *
 * A tokenizer rather than a string of HTML on purpose. The one module allowed to call
 * `dangerouslySetInnerHTML` is `RichText`, and building markup here would either break that
 * rule or need a second sanitizer to make it safe. Returning a list of spans lets React do
 * the escaping, which it does correctly and for free.
 */

export type Token =
  | { kind: 'text'; value: string }
  | { kind: 'url'; value: string; href: string }
  | { kind: 'email'; value: string; href: string }
  | { kind: 'mention'; value: string; memberId: string }

export interface MentionableMember {
  id: string
  displayName: string
}

/**
 * `http(s)://…` and bare `www.…`, plus email addresses.
 *
 * `www.` is included because people write it, and a link that only works when somebody
 * remembered the scheme is a link that usually does not work. It gets `https://` prepended
 * for the href while the text keeps what was typed.
 *
 * No other schemes. `javascript:` and `data:` are how text becomes script, and nothing here
 * needs them — an allow-list of two is the entire defence and it costs nothing.
 */
const PATTERN =
  /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|[^\s<>"'@]+@[^\s<>"'@]+\.[a-z]{2,})/gi

/** Sentence punctuation that trails an address rather than belonging to it. */
const TRAILING = /[.,;:!?)\]}'"]+$/

const escapeRe = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Split text into what to render.
 *
 * Mentions are matched first and longest-name-first, for the same reason the API does it:
 * `@Samantha Reyes` must not resolve to Sam and leave "antha Reyes" behind. Members are
 * optional — pass none and this is purely a linkifier.
 */
export function tokenize(text: string, members: readonly MentionableMember[] = []): Token[] {
  if (!text) return []

  const claimed: { start: number; end: number; token: Token }[] = []

  // People first: an address like sam@x.dev must not be half-eaten by a mention, and a
  // mention must not be mistaken for the local part of an email.
  for (const member of [...members].sort((a, b) => b.displayName.length - a.displayName.length)) {
    if (!member.displayName.trim()) continue
    const re = new RegExp(`@(?:\\[${escapeRe(member.displayName)}\\]|${escapeRe(member.displayName)})(?![\\p{L}\\p{N}])`, 'giu')
    for (const match of text.matchAll(re)) {
      const start = match.index ?? 0
      // An `@` directly after a word is an email's separator, not a mention.
      if (start > 0 && /[\p{L}\p{N}]/u.test(text[start - 1] ?? '')) continue
      if (overlaps(claimed, start, match[0].length)) continue
      claimed.push({
        start,
        end: start + match[0].length,
        token: { kind: 'mention', value: match[0], memberId: member.id },
      })
    }
  }

  for (const match of text.matchAll(PATTERN)) {
    const raw = match[0]
    const trimmed = raw.replace(TRAILING, '')
    if (!trimmed) continue
    const start = match.index ?? 0
    if (overlaps(claimed, start, trimmed.length)) continue

    const isEmail = !trimmed.startsWith('http') && !trimmed.startsWith('www.')
    claimed.push({
      start,
      end: start + trimmed.length,
      token: isEmail
        ? { kind: 'email', value: trimmed, href: `mailto:${trimmed}` }
        : {
            kind: 'url',
            value: trimmed,
            href: trimmed.startsWith('http') ? trimmed : `https://${trimmed}`,
          },
    })
  }

  claimed.sort((a, b) => a.start - b.start)

  const tokens: Token[] = []
  let at = 0
  for (const span of claimed) {
    if (span.start > at) tokens.push({ kind: 'text', value: text.slice(at, span.start) })
    tokens.push(span.token)
    at = span.end
  }
  if (at < text.length) tokens.push({ kind: 'text', value: text.slice(at) })
  return tokens
}

function overlaps(
  claimed: readonly { start: number; end: number }[],
  start: number,
  length: number,
): boolean {
  const end = start + length
  return claimed.some((c) => start < c.end && c.start < end)
}
